import type { APIKey, CreateAPIKeyRequest } from "@/types/dashboard";
import { ServerError } from "@/types/errors";
import { jwtDecode } from "jwt-decode";
import * as Sentry from "@sentry/react";
import type { TimeFilter } from "../../../shared/utils/dateRange";
import type { Plan, Feature } from "../../../shared/entitlements";
import type { FlagMap } from "../../../shared/featureFlags";
import type {
  AnalyticsOverview,
  AnalyticsRange,
} from "../../../shared/services/analytics/overview";

// Re-export the analytics types so UI/hooks can import them from the api module
// (single canonical source — the worker and client share one shape).
export type { AnalyticsOverview, AnalyticsRange };

// Types
export interface Project {
  id: string;
  name: string;
  icon: string;
  color: string;
  sessionCount: number;
  lastUpdated: string;
  lastActivityDate?: string;
  lastSyncDate?: string;
  totalMarkdownSize: number;
  // Teams Step 2: owner attribution for team-visible projects ("shared by X").
  // Present when the project is visible via a team; absent/own when you're the owner.
  ownerId?: string;
  ownerName?: string;
  ownerEmail?: string;
  teamSharing?: string;
  // Teammates holding their own copy of this repo. The listing collapses those copies into
  // one card, so > 1 means the card represents several contributors' workspaces.
  contributorCount?: number;
}

export interface Session {
  id: string;
  clientId: string;
  sessionId: string;
  projectId: string;
  name: string;
  userTitle?: string | null; // User-customized title (takes priority over summary)
  shareStatus?: "private" | "unlisted"; // Share status: private or unlisted
  markdownSize: number;
  rawDataSize: number;
  // Byte size of the SessionData blob; > 0 = cloud-resumable, null/0/absent = legacy.
  // Carried on the wire (SessionSummarySchema) — decoded for the session-row Resume button's
  // resumable gating.
  sessionDataSize?: number | null;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  endedAt?: string;
  metadata: Record<string, unknown>;
  workspaceId: string;
  exchanges?: Exchange[];
  exchangeCount?: number;
  firstExchangeContent?: string;
  etag?: string; // ETag for cache validation
}

export interface SessionDetail {
  id: string;
  clientId: string;
  workspaceId: string;
  sessionId: string;
  projectId: string;
  name: string;
  userTitle?: string | null; // User-customized title (takes priority over summary)
  shareStatus?: "private" | "unlisted"; // Share status: private or unlisted
  markdownContent?: string;
  markdownSize: number;
  rawDataSize: number;
  // Byte size of the SessionData blob; > 0 = cloud-resumable, null/0/absent = legacy.
  // Carried on the wire (SessionDetailSchema) but dropped here previously — restored for the
  // session-page Resume button's resumable gating.
  sessionDataSize?: number | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  endedAt?: string;
  exchanges?: Exchange[];
  // Added from API - now included in initial response for recent sessions
  exchangeCount?: number;
  firstExchangeContent?: string;
  project?: {
    id: string;
    name: string;
    icon?: string;
    color?: string;
  };
  creator?: {
    imageUrl?: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface Exchange {
  id: string;
  sessionId: string;
  content: string;
  orderNumber: number;
  createdAt: string;
  updatedAt: string;
  userTitle?: string | null;
  hiddenFromShare?: boolean;
  splitExchanges?: MessageParts;
  highlightedExchanges?: MessageParts;
  matchesCount?: number;
  metadata: Record<string, unknown>;
}

export interface UserMessage {
  userName: string;
  userMessage: string;
}

export interface MessageParts {
  userMessages: UserMessage[];
  responderName: string;
  response: string;
  userMessagePreview: string;
  responsePreview: string;
}

export interface SearchResult {
  id: string;
  sessionId: string;
  projectId: string;
  name: string;
  userTitle?: string | null;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  endedAt?: string;
  metadata: Record<string, unknown>;
  rank: number;
  matchingExchanges: Exchange[];
  workspaceId: string;
  totalMatches?: number;
  project: Partial<Project>;
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface LoginResponse {
  token: string;
  expiresAt: string;
}

export interface ExtensionLoginRequest {
  session_token: string;
  client: string;
  client_version?: string;
  hostname?: string;
  os?: string;
  os_version?: string;
  os_display_name?: string;
  architecture?: string;
  username?: string;
}

export interface ExtensionLoginResponse {
  clerkToken: string;
  refreshToken: string;
  createdAt: string;
  expiresAt: string;
  user: {
    email: string;
  };
}

export interface CLIAuthCode {
  code: string;
  expiresAt: string;
}

export interface CLIAuthCodeResponse {
  success: boolean;
  error?: string;
  response?: CLIAuthCode;
}

export interface SearchResponse {
  results: SearchResult[];
  total: number;
  query: string;
  currentPage: number;
  totalItems: number;
  totalPages: number;
  limit: number;
  groupSize: number;
}

// API Base URL
const API_BASE = window.location.origin;

// Store the custom JWT token
let customJwtToken: string | null = null;
// Store the function to get a Clerk token
let getClerkTokenFunction: (options?: {
  template?: string;
}) => Promise<string | null>;

/**
 * Base path for the analytics endpoints: the caller's own, or — in superadmin
 * "view as user" mode — the email-gated admin variant for `ownerId`. The two
 * route families serve identical payloads from the same handlers.
 */
const analyticsBase = (ownerId?: string): string =>
  ownerId
    ? `${API_BASE}/api/v1/admin/users/${encodeURIComponent(ownerId)}/analytics`
    : `${API_BASE}/api/v1/analytics`;

/** The page-level analytics filter (agent/project) shared across overview,
 * ledger, and spec-score endpoints. */
export interface AnalyticsFilterParams {
  agent?: string | null;
  project?: string | null;
}

/** Serialize the filter as trailing `&agent=&project=` query params (empty when
 * unset), so callers append it to an existing `?range=…` query string. */
function analyticsFilterQuery(filter?: AnalyticsFilterParams): string {
  let q = "";
  if (filter?.agent) q += `&agent=${encodeURIComponent(filter.agent)}`;
  if (filter?.project) q += `&project=${encodeURIComponent(filter.project)}`;
  return q;
}

// Helper function to get auth headers
const getAuthHeaders = (): HeadersInit => {
  return {
    "Content-Type": "application/json",
    ...(customJwtToken && { Authorization: `Bearer ${customJwtToken}` }),
  };
};

async function refreshToken(
  getClerkToken: (options?: { template?: string }) => Promise<string | null>
) {
  customJwtToken = null;
  const clerkToken = await getClerkToken({ template: "specstory_cloud" });
  if (!clerkToken) {
    console.error("No Clerk session token available, forcing logout");
    window.location.href = "/logout";
    return;
  }
  await loginWithClerk(clerkToken);
}

// Optional callback for handling server errors
let serverErrorHandler: ((error: Error) => void) | null = null;

export function setServerErrorHandler(
  handler: ((error: Error) => void) | null
) {
  serverErrorHandler = handler;
}

async function authedFetch(
  url: string,
  options: RequestInit
): Promise<Response> {
  // Try to load the requested resource
  const response = await fetch(url, options);

  // Check for server errors (5xx)
  if (response.status >= 500 && response.status < 600) {
    // Try to get the actual error message from the response body
    let errorMessage: string | undefined;

    try {
      const contentType = response.headers.get("content-type");
      // Only try to parse if we have JSON content and body hasn't been read
      if (
        contentType &&
        contentType.includes("application/json") &&
        !response.bodyUsed
      ) {
        // Clone the response so we can still return it later
        const clonedResponse = response.clone();
        const errorData = await clonedResponse.json();

        // Check for common error message formats
        errorMessage = errorData.error || errorData.message || errorData.detail;
      }
    } catch (e) {
      // If parsing fails, we'll use the default message
      // In production, you might want to log this to Sentry
      if (import.meta.env.DEV) {
        console.warn("Failed to parse error response:", e);
      }
    }

    // Create a ServerError with the status code and message
    const error = new ServerError(
      response.status,
      response.statusText,
      errorMessage
    );

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error(`Server error at ${url}:`, error);
    } else {
      // In production, capture to Sentry for monitoring
      Sentry.captureException(error, {
        extra: {
          url,
          method: options.method || "GET",
          statusCode: response.status,
          statusText: response.statusText,
        },
        tags: {
          type: "server_error",
        },
      });
    }

    if (serverErrorHandler) {
      serverErrorHandler(error);
    }
    // Still return the response so callers can handle it if needed
    return response;
  }

  // In case of 401, attempt to refresh, force logout and error if we can't get a Clerk token
  if (response.status === 401) {
    if (!getClerkTokenFunction) {
      window.location.href = "/logout";
      throw new Error("No Clerk token function available, forcing logout");
    }
    await refreshToken(getClerkTokenFunction);
    // Repeat the request after the token was refreshed
    return await fetch(url, options);
  }
  return response;
}

// Login function to exchange Clerk token for custom JWT
export async function loginWithClerk(
  clerkToken: string
): Promise<LoginResponse> {
  try {
    const response = await fetch(`${API_BASE}/api/v1/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ sessionToken: clerkToken }),
    });

    const data: ApiResponse<LoginResponse> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Login failed");
    }

    // Store the custom JWT token
    customJwtToken = data.data!.token;

    return data.data!;
  } catch (error) {
    throw new Error(
      `Login failed: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function extensionLogin(
  clerkToken: string,
  client: string,
  client_version?: string,
  hostname?: string,
  os?: string,
  os_version?: string,
  os_display_name?: string,
  architecture?: string,
  username?: string
): Promise<ExtensionLoginResponse> {
  try {
    const requestData: ExtensionLoginRequest = {
      session_token: clerkToken,
      client: client,
      client_version,
      hostname,
      os,
      os_version,
      os_display_name,
      architecture,
      username,
    };
    const response = await authedFetch(`${API_BASE}/api/v1/extension-login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const body = await response.text();
      const errorMessage = `Extension login failed: ${response.statusText} (${response.status}).${import.meta.env.DEV ? `\nBody: ${body}` : ""}`;
      console.error(errorMessage);
      throw new Error(errorMessage, { cause: body });
    }

    const data: ExtensionLoginResponse = await response.json();

    const responseData: ExtensionLoginResponse = {
      // FIXME: The extension still needs the clerk token for old share.specstory.com, remove once we dismiss old share
      clerkToken: clerkToken,
      refreshToken: data.refreshToken,
      createdAt: data.createdAt,
      expiresAt: data.expiresAt,
      user: data.user,
    };

    return responseData;
  } catch (error) {
    throw new Error(
      `Extension login failed: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

// Function to set the custom JWT token (for testing or manual token management)
export function setCustomJwtToken(token: string) {
  customJwtToken = token;
}

// Function to clear the custom JWT token
export function clearCustomJwtToken() {
  customJwtToken = null;
}

// Function to ensure we have a valid custom JWT token
export async function ensureAuthenticated(
  getClerkToken: (options?: { template?: string }) => Promise<string | null>
) {
  getClerkTokenFunction = getClerkToken;
  if (customJwtToken) {
    const decoded = jwtDecode(customJwtToken);
    if (decoded.exp && decoded.exp < Date.now() / 1000) {
      await refreshToken(getClerkTokenFunction);
    }
  }
  if (customJwtToken) {
    return; // Already have a token
  }

  const clerkToken = await getClerkToken({ template: "specstory_cloud" });
  if (!clerkToken) {
    throw new Error("No Clerk session token available");
  }

  await loginWithClerk(clerkToken);
}

// Helper to check if we have a custom JWT token
export function hasCustomToken(): boolean {
  return customJwtToken !== null;
}

// API Key Management functions
export async function listAPIKeys(): Promise<APIKey[]> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(`${API_BASE}/api/v1/api-tokens`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to list API keys");
    }

    // The backend returns { success: true, tokens: APIKey[] }
    return data.tokens || [];
  } catch (error) {
    throw new Error(
      `Failed to list API keys: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function createAPIKey(
  request: CreateAPIKeyRequest
): Promise<{ token: string; tokenData: APIKey }> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(`${API_BASE}/api/v1/api-tokens`, {
      method: "POST",
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to create API key");
    }

    // The backend returns { success: true, token: string, tokenData: APIKey }
    return {
      token: data.token,
      tokenData: data.tokenData,
    };
  } catch (error) {
    throw new Error(
      `Failed to create API key: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function updateAPIKeyName(
  id: string,
  name: string
): Promise<APIKey> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(`${API_BASE}/api/v1/api-tokens/${id}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to update API key");
    }

    // The backend returns { success: true, token: APIKey }
    return data.token;
  } catch (error) {
    throw new Error(
      `Failed to update API key: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function deleteAPIKey(id: string): Promise<void> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(`${API_BASE}/api/v1/api-tokens/${id}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to delete API key");
    }
  } catch (error) {
    throw new Error(
      `Failed to delete API key: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

// ── Teams ─────────────────────────────────────────────────────────────────────
// Mirrors the api-tokens block: authedFetch + getAuthHeaders, {success,data} envelope
// (unwrap data.data). fetchInvite is public (plain fetch, no JWT), like
// fetchSharedSession. Shapes match the worker serializers in handlers/teams.ts.

export type TeamRole = "admin" | "member";

export interface Team {
  id: string;
  slug: string;
  name: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  role?: TeamRole; // caller's role (present on create + team detail)
}

export interface TeamMember {
  id: number;
  userId: string | null;
  invitedEmail: string | null;
  email: string | null; // cached Clerk email, falling back to the invite email
  name: string | null; // cached Clerk display name
  imageUrl: string | null; // cached Clerk avatar
  status: "invited" | "active";
  role: TeamRole;
  joinedAt: string | null;
  createdAt: string;
  inviteLink?: string; // admin-only, pending invites
}

export interface TeamSummary {
  id: string;
  slug: string;
  name: string;
  createdBy: string;
  role: TeamRole;
  joinedAt: string | null;
}

export interface PendingInvite {
  id: number;
  teamId: string;
  teamSlug: string;
  teamName: string;
  role: TeamRole;
  invitedEmail: string | null;
  createdAt: string;
}

export interface MyTeams {
  teams: TeamSummary[];
  invites: PendingInvite[];
}

export interface TeamDetail {
  team: Team;
  members: TeamMember[];
}

export interface InvitePreview {
  team: { slug: string; name: string };
  invitedEmail: string | null;
  invitedBy: string;
  role: TeamRole;
}

export interface ShareableWorkspace {
  id: string;
  projectId: string;
  name: string;
}

export interface AcceptInviteOptions {
  makePrivateWorkspaceIds?: string[];
  optOutWorkspaceIds?: string[];
}

export interface InviteResult {
  email: string;
  status: "invited" | "skipped";
  inviteId?: number;
  inviteLink?: string;
  reason?: string;
}

export async function fetchTeams(): Promise<MyTeams> {
  const response = await authedFetch(`${API_BASE}/api/v1/teams`, {
    headers: getAuthHeaders(),
  });
  const data: ApiResponse<MyTeams> = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to fetch teams");
  return data.data!;
}

export async function createTeam(params: {
  name: string;
  slug: string;
}): Promise<Team> {
  const response = await authedFetch(`${API_BASE}/api/v1/teams`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(params),
  });
  const data: ApiResponse<Team> = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to create team");
  return data.data!;
}

export async function fetchTeam(slug: string): Promise<TeamDetail> {
  const response = await authedFetch(
    `${API_BASE}/api/v1/teams/${encodeURIComponent(slug)}`,
    { headers: getAuthHeaders() }
  );
  const data: ApiResponse<TeamDetail> = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to fetch team");
  return data.data!;
}

export async function updateTeam(
  slug: string,
  patch: { name?: string; slug?: string }
): Promise<Team> {
  const response = await authedFetch(
    `${API_BASE}/api/v1/teams/${encodeURIComponent(slug)}`,
    { method: "PATCH", headers: getAuthHeaders(), body: JSON.stringify(patch) }
  );
  const data: ApiResponse<Team> = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to update team");
  return data.data!;
}

export async function deleteTeam(slug: string): Promise<void> {
  const response = await authedFetch(
    `${API_BASE}/api/v1/teams/${encodeURIComponent(slug)}`,
    { method: "DELETE", headers: getAuthHeaders() }
  );
  const data: ApiResponse<unknown> = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to delete team");
}

export async function inviteMembers(
  slug: string,
  params: { emails: string[]; role?: TeamRole }
): Promise<InviteResult[]> {
  const response = await authedFetch(
    `${API_BASE}/api/v1/teams/${encodeURIComponent(slug)}/invites`,
    { method: "POST", headers: getAuthHeaders(), body: JSON.stringify(params) }
  );
  const data: ApiResponse<{ invites: InviteResult[] }> = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to send invites");
  return data.data!.invites;
}

export async function revokeInvite(
  slug: string,
  inviteId: number
): Promise<void> {
  const response = await authedFetch(
    `${API_BASE}/api/v1/teams/${encodeURIComponent(slug)}/invites/${inviteId}`,
    { method: "DELETE", headers: getAuthHeaders() }
  );
  const data: ApiResponse<unknown> = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to revoke invite");
}

export async function updateMemberRole(
  slug: string,
  userId: string,
  role: TeamRole
): Promise<void> {
  const response = await authedFetch(
    `${API_BASE}/api/v1/teams/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}`,
    { method: "PATCH", headers: getAuthHeaders(), body: JSON.stringify({ role }) }
  );
  const data: ApiResponse<unknown> = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to update member role");
}

export async function removeMember(
  slug: string,
  userId: string
): Promise<void> {
  const response = await authedFetch(
    `${API_BASE}/api/v1/teams/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}`,
    { method: "DELETE", headers: getAuthHeaders() }
  );
  const data: ApiResponse<unknown> = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to remove member");
}

/** Public invite preview — no JWT (plain fetch), 404-aware. Mirrors fetchSharedSession. */
export async function fetchInvite(token: string): Promise<InvitePreview> {
  const response = await fetch(
    `${API_BASE}/api/v1/invites/${encodeURIComponent(token)}`,
    { headers: { "Content-Type": "application/json" } }
  );
  if (response.status === 404) {
    throw new Error("This invite is not valid or has already been used");
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch invite: ${response.statusText}`);
  }
  const data: ApiResponse<InvitePreview> = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to fetch invite");
  return data.data!;
}

export async function acceptInvite(
  token: string,
  opts?: AcceptInviteOptions
): Promise<{ teamId: string; status: string }> {
  const response = await authedFetch(
    `${API_BASE}/api/v1/invites/${encodeURIComponent(token)}/accept`,
    { method: "POST", headers: getAuthHeaders(), body: JSON.stringify(opts ?? {}) }
  );
  const data: ApiResponse<{ teamId: string; status: string }> =
    await response.json();
  if (!data.success) throw new Error(data.error || "Failed to accept invite");
  return data.data!;
}

/** The caller's own `shared` workspaces — what would become team-visible on joining. */
export async function fetchShareableWorkspaces(): Promise<ShareableWorkspace[]> {
  const response = await authedFetch(
    `${API_BASE}/api/v1/teams/shareable-workspaces`,
    { headers: getAuthHeaders() }
  );
  const data: ApiResponse<{ workspaces: ShareableWorkspace[] }> =
    await response.json();
  if (!data.success)
    throw new Error(data.error || "Failed to load shareable workspaces");
  return data.data!.workspaces;
}

// ── Per-project team-sharing controls (owner-only) ────────────────────────────

export interface TeamOptout {
  teamId: string;
  teamSlug: string;
  teamName: string;
  createdAt: string;
}

/** Flip a project's workspace-level team-sharing switch ('shared' | 'private'). */
export async function updateProjectSharing(
  projectId: string,
  teamSharing: "shared" | "private"
): Promise<void> {
  const response = await authedFetch(
    `${API_BASE}/api/v1/projects/${encodeURIComponent(projectId)}`,
    {
      method: "PATCH",
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ teamSharing }),
    }
  );
  const data: ApiResponse<unknown> = await response.json();
  if (!data.success)
    throw new Error(data.error || "Failed to update team sharing");
}

export async function fetchTeamOptouts(
  projectId: string
): Promise<TeamOptout[]> {
  const response = await authedFetch(
    `${API_BASE}/api/v1/projects/${encodeURIComponent(projectId)}/team-optouts`,
    { headers: getAuthHeaders() }
  );
  const data: ApiResponse<{ optouts: TeamOptout[] }> = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to load exclusions");
  return data.data!.optouts;
}

export async function putTeamOptout(
  projectId: string,
  teamSlug: string
): Promise<void> {
  const response = await authedFetch(
    `${API_BASE}/api/v1/projects/${encodeURIComponent(projectId)}/team-optouts/${encodeURIComponent(teamSlug)}`,
    { method: "PUT", headers: getAuthHeaders() }
  );
  const data: ApiResponse<unknown> = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to exclude team");
}

export async function deleteTeamOptout(
  projectId: string,
  teamSlug: string
): Promise<void> {
  const response = await authedFetch(
    `${API_BASE}/api/v1/projects/${encodeURIComponent(projectId)}/team-optouts/${encodeURIComponent(teamSlug)}`,
    { method: "DELETE", headers: getAuthHeaders() }
  );
  const data: ApiResponse<unknown> = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to re-share team");
}

// API functions
export async function fetchProjects(): Promise<Project[]> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(`${API_BASE}/api/v1/projects`, {
      headers,
    });
    const data: ApiResponse<{ projects: Project[] }> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch projects");
    }

    return data.data!.projects;
  } catch (error) {
    throw new Error(
      `Failed to load projects: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function fetchProject(projectId: string): Promise<Project | null> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(
      `${API_BASE}/api/v1/projects/${encodeURIComponent(projectId)}`,
      { headers }
    );

    if (response.status === 404) {
      return null;
    }

    const data: ApiResponse<{ project: Project }> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch project");
    }

    return data.data?.project || null;
  } catch (error) {
    throw new Error(
      `Failed to load project: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function fetchSessions(projectId: string): Promise<Session[]> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(
      `${API_BASE}/api/v1/projects/${encodeURIComponent(projectId)}/sessions`,
      {
        headers,
      }
    );
    const data: ApiResponse<{ sessions: Session[] }> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch sessions");
    }

    return data.data!.sessions;
  } catch (error) {
    throw new Error(
      `Failed to load sessions: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function fetchRecentSessions(
  limit: number = 5
): Promise<SessionDetail[]> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(
      `${API_BASE}/api/v1/sessions/recent?limit=${limit}`,
      { headers }
    );

    const data: ApiResponse<{
      sessions: Array<
        Session & {
          workspace: {
            id: string;
            name: string;
            icon?: string;
            color?: string;
          };
        }
      >;
    }> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch recent sessions");
    }

    return data.data?.sessions || [];
  } catch (error) {
    throw new Error(
      `Failed to load recent sessions: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function updateProjectStyle(
  projectId: string,
  icon: string,
  color: string
): Promise<void> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(
      `${API_BASE}/api/v1/projects/${encodeURIComponent(projectId)}`,
      {
        method: "PATCH",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ icon, color }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to update project style");
    }
  } catch (error) {
    console.error("Error updating project style:", error);
    throw error;
  }
}

export async function deleteProject(projectId: string): Promise<void> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(
      `${API_BASE}/api/v1/projects/${encodeURIComponent(projectId)}`,
      {
        method: "DELETE",
        headers,
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to delete project: ${response.statusText}`);
    }
  } catch (error) {
    throw new Error(
      `Failed to delete project: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function deleteSession(
  projectId: string,
  sessionId: string
): Promise<void> {
  try {
    const headers = getAuthHeaders();
    const url = `${API_BASE}/api/v1/projects/${encodeURIComponent(projectId)}/sessions/${encodeURIComponent(sessionId)}`;

    const response = await authedFetch(url, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to delete session: ${response.statusText}`);
    }
  } catch (error) {
    throw new Error(
      `Failed to delete session: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function fetchSessionDetail(
  projectId: string,
  sessionId: string
): Promise<SessionDetail> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(
      `${API_BASE}/api/v1/projects/${encodeURIComponent(
        projectId
      )}/sessions/${encodeURIComponent(sessionId)}`,
      {
        headers,
      }
    );
    const data: ApiResponse<{ session: SessionDetail }> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch session detail");
    }

    return data.data!.session;
  } catch (error) {
    throw new Error(
      `Failed to load session detail: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

/**
 * Fetch a publicly shared session (no authentication required)
 * This endpoint filters out exchanges marked as hidden from share
 */
export async function fetchSharedSession(
  sessionId: string
): Promise<SessionDetail> {
  try {
    const response = await fetch(
      `${API_BASE}/api/v1/share/${encodeURIComponent(sessionId)}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    // Handle 404 for non-existent or private sessions
    if (response.status === 404) {
      throw new Error("This session is not shared or does not exist");
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch shared session: ${response.statusText}`);
    }

    const data: ApiResponse<{ session: SessionDetail }> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch shared session");
    }

    return data.data!.session;
  } catch (error) {
    throw new Error(
      `Failed to load shared session: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function updateSessionName(
  projectId: string,
  sessionId: string,
  userTitle: string
): Promise<SessionDetail> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(
      `${API_BASE}/api/v1/projects/${encodeURIComponent(
        projectId
      )}/sessions/${encodeURIComponent(sessionId)}`,
      {
        method: "PATCH",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userTitle }),
      }
    );

    if (!response.ok) {
      const errorData: ApiResponse = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to update session title: ${response.statusText}`
      );
    }

    const data: ApiResponse<{ session: SessionDetail }> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to update session name");
    }

    return data.data!.session;
  } catch (error) {
    throw new Error(
      `Failed to update session name: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function updateSessionShareStatus(
  projectId: string,
  sessionId: string,
  shareStatus: "private" | "unlisted"
): Promise<SessionDetail> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(
      `${API_BASE}/api/v1/projects/${encodeURIComponent(
        projectId
      )}/sessions/${encodeURIComponent(sessionId)}`,
      {
        method: "PATCH",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ shareStatus }),
      }
    );

    if (!response.ok) {
      const errorData: ApiResponse = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to update session share status: ${response.statusText}`
      );
    }

    const data: ApiResponse<{ session: SessionDetail }> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to update session share status");
    }

    return data.data!.session;
  } catch (error) {
    throw new Error(
      `Failed to update session share status: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function updateExchangeName(
  projectId: string,
  sessionId: string,
  exchangeId: string,
  name: string
): Promise<Exchange> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(
      `${API_BASE}/api/v1/projects/${encodeURIComponent(
        projectId
      )}/sessions/${encodeURIComponent(sessionId)}/exchanges/${encodeURIComponent(exchangeId)}`,
      {
        method: "PATCH",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name }),
      }
    );

    if (!response.ok) {
      const errorData: ApiResponse = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to update exchange name: ${response.statusText}`
      );
    }

    const data: ApiResponse<{ exchange: Exchange }> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to update exchange name");
    }

    return data.data!.exchange;
  } catch (error) {
    throw new Error(
      `Failed to update exchange name: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function updateExchangeVisibility(
  projectId: string,
  sessionId: string,
  exchangeId: string,
  hidden: boolean
): Promise<Exchange> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(
      `${API_BASE}/api/v1/projects/${encodeURIComponent(
        projectId
      )}/sessions/${encodeURIComponent(sessionId)}/exchanges/${encodeURIComponent(exchangeId)}`,
      {
        method: "PATCH",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ hidden }),
      }
    );

    if (!response.ok) {
      const errorData: ApiResponse = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to update exchange visibility: ${response.statusText}`
      );
    }

    const data: ApiResponse<{ exchange: Exchange }> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to update exchange visibility");
    }

    return data.data!.exchange;
  } catch (error) {
    throw new Error(
      `Failed to update exchange visibility: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function searchMarkdown(
  query: string,
  projectId?: string
): Promise<{ results: SearchResult[]; total: number; query: string }> {
  try {
    const headers = getAuthHeaders();
    const url = new URL(`${API_BASE}/api/v1/search`);
    url.searchParams.set("q", query);
    if (projectId) {
      url.searchParams.set("projectId", projectId);
    }

    const response = await authedFetch(url.toString(), {
      headers,
    });
    const data: ApiResponse<{
      results: SearchResult[];
      total: number;
      query: string;
    }> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Search failed");
    }

    return data.data!;
  } catch (error) {
    throw new Error(
      `Search failed: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

const MIN_EXCHANGE_PER_GROUP = 2;
const MAX_EXCHANGE_PER_GROUP = 20;
const PAGE_MARGINS = 180 + 102 + 16; // Top/bottom margins of the page around the list of sessions
const SESSION_MARGINS = 92 + 26; // Top/bottom margins of the session card
const GROUP_HEIGHT = 142; // Height of the exchange includede the bottom margin
const calculateGroupSize = (results: SearchResult[]) => {
  // Each result is a session that can contains from 1 to n exchanges
  // Since we display the results grouped by session, we want to show a variable number of exchanges when the group is collapsed (initially)
  // We want a minimum of MIN_EXCHANGE_PER_GROUP exchanges per group (1 if there is only 1)
  // We want a maximum of MAX_EXCHANGE_PER_GROUP exchanges per group
  // We calculate the page height starting from a group of 2,
  // calculate the height given the group and increment the group size until
  // we exceeds the available height (so we fill at least 1 screen)

  let attemptGroupSize = MIN_EXCHANGE_PER_GROUP;
  const winHeight = window.innerHeight;
  const height = winHeight - PAGE_MARGINS;
  let groupOk = false;
  while (!groupOk) {
    const exchangesHeight = results.reduce(
      (tot: number, session: SearchResult) => {
        const sessionItems = Math.min(
          session.matchingExchanges.length,
          attemptGroupSize
        );
        return tot + SESSION_MARGINS + sessionItems * GROUP_HEIGHT;
      },
      0
    );
    if (
      exchangesHeight <= height &&
      attemptGroupSize < MAX_EXCHANGE_PER_GROUP
    ) {
      attemptGroupSize++;
    } else {
      groupOk = true;
    }
  }
  return attemptGroupSize;
};

// Exported so a test can validate it against the worker's SDL (worker/graphql_schema.ts).
// Nothing else checks the two agree: a field renamed on the server or misspelled here is a
// runtime GraphQL error on a live search, not a compile error.
export const SEARCH_SESSIONS_QUERY = `
  query SearchSessions($query: String!, $filters: SessionFilters, $limit: Int, $page: Int) {
    searchSessions(query: $query, filters: $filters, limit: $limit, page: $page) {
      results {
        id
        sessionId
        name
        userTitle
        createdAt
        updatedAt
        startedAt
        endedAt
        metadata {
          clientName
          clientVersion
          agentName
          deviceId
          gitBranches
          llmModels
          tags
          summary
        }
        rank
        matchingExchanges {
          id
          sessionId
          content
          orderNumber
          createdAt
          updatedAt
        }
        project {
          id
          name
          icon
          color
          ownerId
          ownerName
          ownerEmail
          contributorCount
        }
      }
      total
      query
      projectId
    }
  }
`;

export async function searchMarkdownGraphQL(
  query: string,
  projectIds?: string[],
  timeFilter?: TimeFilter,
  agentNames?: string[],
  limit: number = 200,
  page: number = 1
): Promise<SearchResponse> {
  try {
    const headers = getAuthHeaders();

    const graphqlQuery = SEARCH_SESSIONS_QUERY;

    const variables: Record<string, unknown> = {
      query,
      limit,
      page,
    };

    if (projectIds && projectIds.length > 0) {
      variables.filters = { projectIds };
    }

    if (timeFilter) {
      variables.filters = {
        ...(variables.filters || {}),
        timeFilter,
      };
    }

    if (agentNames && agentNames.length > 0) {
      variables.filters = {
        ...(variables.filters || {}),
        agentNames,
      };
    }

    const response = await authedFetch(`${API_BASE}/api/v1/graphql`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        query: graphqlQuery,
        variables,
      }),
    });

    if (!response.ok) {
      throw new Error("GraphQL query failed");
    }

    const result = await response.json();

    if (result.errors) {
      throw new Error(result.errors[0]?.message || "GraphQL query failed");
    }

    const searchResult = result.data.searchSessions;

    return {
      results: searchResult.results,
      total: searchResult.total,
      query: searchResult.query,
      currentPage: page,
      totalItems: searchResult.total,
      totalPages: Math.ceil(searchResult.total / limit),
      limit: limit,
      groupSize: calculateGroupSize(searchResult.results),
    };
  } catch (error) {
    throw new Error(
      `GraphQL search failed: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function getCLIAuthCode(
  token: string
): Promise<CLIAuthCodeResponse> {
  // Make API call to get or create auth code
  const response = await fetch("/api/v1/device-auth-code", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    return {
      success: false,
      error: `Failed to get auth code: ${response.statusText}`,
    };
  }

  const data: CLIAuthCode = await response.json();

  if (!data.code) {
    return {
      success: false,
      error: "Failed to get auth code",
    };
  }
  return {
    success: true,
    response: data,
  };
}

export async function fetchSessionMarkdown(
  projectId: string,
  sessionId: string
) {
  const response = await fetch(
    `${API_BASE}/api/v1/projects/${projectId}/sessions/${sessionId}`,
    {
      method: "GET",
      headers: {
        ...getAuthHeaders(),
        Accept: "text/markdown",
      },
    }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch session markdown: ${response.statusText}`);
  }
  return response.text();
}

/**
 * Fetch markdown for a publicly shared session (no authentication required)
 */
export async function fetchSharedSessionMarkdown(
  sessionId: string
): Promise<string> {
  try {
    const response = await fetch(
      `${API_BASE}/api/v1/share/${encodeURIComponent(sessionId)}/markdown`,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    // Handle 404 for non-existent or private sessions
    if (response.status === 404) {
      throw new Error("This session is not shared or does not exist");
    }

    if (!response.ok) {
      throw new Error(
        `Failed to fetch shared session markdown: ${response.statusText}`
      );
    }

    return response.text();
  } catch (error) {
    throw new Error(
      `Failed to load shared session markdown: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

// RAG Chat Types
export interface RAGContextChunk {
  chunk_id: string;
  exchange_id: string;
  content: string;
  relevance_score: number;
  project_id: string;
  session_id: string;
  session_client_id: string;
  project_name: string;
  project_icon?: string;
  project_color?: string;
  conversation_file: string;
  metadata: {
    conversation_title: string;
    is_chunked: boolean;
    chunk_index?: number;
    total_chunks?: number;
    workspace_name: string;
  };
}

export interface RAGResponse {
  question: string;
  rewritten_query: string | null;
  response: string;
  context_chunks: RAGContextChunk[];
  metadata: {
    num_chunks: number;
    projects_searched: string[];
    session_id: string;
    used_reranking: boolean;
    processing_time_ms?: number;
  };
}

// Fetch existing chat session by ID
// Define proper types for chat session
export interface ChatSession {
  id: string;
  title?: string;
  created_at: string;
  updated_at: string;
  user_id?: string;
  messages_count?: number | string; // Added from API - can be number or string depending on backend
  messages?: Array<{
    id: string;
    query: string;
    response: string;
    created_at: string;
    metadata?: RAGResponse;
  }>;
}

export async function fetchChatSession(chatSessionId: string): Promise<{
  chatSession: ChatSession;
  messages?: Array<{
    id: string;
    query: string;
    response: string;
    created_at: string;
    metadata?: RAGResponse;
    message_chunks?: Array<{
      id?: string;
      chunk_id?: string;
      exchange_chunk_id?: string;
      exchange_id: string;
      content?: string;
      relevance_score?: number;
      project_id: string;
      session_id: string;
      session_client_id?: string;
      session_name?: string;
      session_summary?: string;
      project_name?: string;
      project_icon?: string;
      project_color?: string;
      conversation_file?: string;
      metadata?: Record<string, unknown>;
    }>; // Properly typed chunks from backend
    // exchange_chunk_ids the server withheld because the workspace behind them is no longer
    // visible to this user. The saved response text still cites them, so the renderer needs
    // them to distinguish "source revoked" from "model cited nothing here".
    unavailable_chunk_ids?: string[];
  }>;
} | null> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(
      `${API_BASE}/api/v1/chat/${chatSessionId}`,
      {
        method: "GET",
        headers,
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return null; // Chat not found
      }
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.error ||
          `Failed to fetch chat session: ${response.statusText}`
      );
    }

    const data: ApiResponse<{
      chatSession: ChatSession;
      messages?: Array<{
        id: string;
        query: string;
        response: string;
        created_at: string;
        metadata?: RAGResponse;
      }>;
    }> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch chat session");
    }

    return data.data || null;
  } catch (error) {
    console.error("Error fetching chat session:", error);
    if (error instanceof Error && error.message.includes("not found")) {
      return null;
    }
    throw error;
  }
}

// Fetch all chat sessions for the authenticated user
export async function fetchChatSessions({
  limit,
}: { limit?: number } = {}): Promise<ChatSession[]> {
  try {
    const url = new URL(`${API_BASE}/api/v1/chats`);
    if (limit) {
      url.searchParams.set("limit", limit.toString());
    }
    const headers = getAuthHeaders();
    const response = await authedFetch(url.toString(), {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.error ||
          `Failed to fetch chat sessions: ${response.statusText}`
      );
    }

    const data: ApiResponse<{
      chatSessions: ChatSession[];
    }> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch chat sessions");
    }

    // Normalize messages_count to always be a number at the API boundary
    // Backend may return it as string (from SQL COUNT) or number
    return (data.data?.chatSessions || []).map((session) => ({
      ...session,
      messages_count:
        typeof session.messages_count === "string"
          ? parseInt(session.messages_count, 10) || 0
          : session.messages_count || 0,
    }));
  } catch (error) {
    console.error("Error fetching chat sessions:", error);
    throw error;
  }
}

// Delete a chat session
export async function deleteChatSession(chatSessionId: string): Promise<void> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(
      `${API_BASE}/api/v1/chat/${chatSessionId}`,
      {
        method: "DELETE",
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.error ||
          `Failed to delete chat session: ${response.statusText}`
      );
    }

    const data: ApiResponse = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to delete chat session");
    }
  } catch (error) {
    console.error("Error deleting chat session:", error);
    throw error;
  }
}

// RAG Chat API function
export async function chatWithRAG(
  query: string,
  chatSessionId?: string,
  projectIds?: string[],
  timeFilter?: TimeFilter,
  agentNames?: string[]
): Promise<RAGResponse> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(`${API_BASE}/api/v1/chat`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        query,
        chatSessionId,
        projectIds,
        timeFilter,
        agentNames,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.error || `Chat request failed: ${response.statusText}`
      );
    }

    const data: ApiResponse<
      | { chatSessionId: string; response: string; type: string }
      | string
      | RAGResponse
    > = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Chat request failed");
    }

    // Handle the new response format: { chatSessionId, response, type }
    if (
      data.data &&
      typeof data.data === "object" &&
      "response" in data.data &&
      "chatSessionId" in data.data
    ) {
      // New format with chatSessionId and response
      const responseData = data.data as {
        chatSessionId: string;
        response: string;
      };
      const { chatSessionId: receivedSessionId, response: responseText } =
        responseData;

      return {
        question: query,
        rewritten_query: null,
        response: responseText,
        context_chunks: [],
        metadata: {
          num_chunks: 0,
          projects_searched: projectIds || [],
          session_id: receivedSessionId || chatSessionId || "", // Use received session ID
          used_reranking: false,
        },
      };
    } else if (typeof data.data === "string") {
      // Backward compatibility: If it's just a string
      return {
        question: query,
        rewritten_query: null,
        response: data.data,
        context_chunks: [],
        metadata: {
          num_chunks: 0,
          projects_searched: projectIds || [],
          session_id: chatSessionId || "",
          used_reranking: false,
        },
      };
    }

    // If data.data is already a full RAGResponse object
    return data.data as RAGResponse;
  } catch (error) {
    throw new Error(
      `Chat request failed: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

// RAG Chat API function with streaming support
interface StreamingStepEvent {
  type: string;
  message: string;
  sessionId?: string;
  originalQuery?: string;
  rewrittenQuery?: string;
}

interface StreamingContextEvent {
  chatSessionId?: string;
  type: string;
  response?: string;
  exchanges?: Array<{
    exchange_id: string;
    chunk_id?: string;
    project_id: string;
    session_id: string;
    session_client_id: string;
    project_name?: string;
    session_name?: string;
    relevance_score?: number;
    [key: string]: unknown;
  }>;
}

// User Tools API function
export interface UserToolStats {
  agentName: string;
  sessionCount: number;
  lastUsed: string;
}

export async function fetchUserTools(): Promise<UserToolStats[]> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(`${API_BASE}/api/v1/user/tools`, {
      headers,
    });
    const data: ApiResponse<{ tools: UserToolStats[] }> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch user tools");
    }

    return data.data?.tools || [];
  } catch (error) {
    throw new Error(
      `Failed to load user tools: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

// First-sync detection for the connect-a-tool flow
export interface ToolConnectionSession {
  id: string;
  name: string;
  projectId: string;
  projectName: string;
  projectIcon: string | null;
  projectColor: string | null;
}

export interface ToolConnectionStatus {
  // Opaque server timestamp. The first call (no `cursor`) only returns this; pass it back
  // on every later call to ask for sessions first synced after it.
  cursor: string;
  session: ToolConnectionSession | null;
}

export async function fetchToolConnection(
  agentName: string,
  cursor?: string
): Promise<ToolConnectionStatus> {
  const params = new URLSearchParams({ agentName });
  if (cursor) params.set("cursor", cursor);

  const response = await authedFetch(
    `${API_BASE}/api/v1/user/tools/connection?${params}`,
    { headers: getAuthHeaders() }
  );
  const data: ApiResponse<ToolConnectionStatus> = await response.json();

  if (!data.success || !data.data) {
    throw new Error(data.error || "Failed to check tool connection");
  }

  return data.data;
}

/**
 * Cross-agent Analytics Overview (Phase 1). Returns the pre-bucketed payload
 * the worker aggregates server-side. JWT-authed; workspace-scoped.
 */
export async function fetchAnalyticsOverview(
  range: AnalyticsRange,
  ownerId?: string,
  filter?: AnalyticsFilterParams
): Promise<AnalyticsOverview> {
  try {
    const headers = getAuthHeaders();
    // Browser IANA zone → the server buckets the "when you code" punchcard in
    // the user's local time (everything else stays UTC).
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await authedFetch(
      `${analyticsBase(ownerId)}/overview?range=${range}&tz=${encodeURIComponent(tz)}${analyticsFilterQuery(filter)}`,
      { headers }
    );
    const data: ApiResponse<{ overview: AnalyticsOverview }> =
      await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch analytics overview");
    }
    if (!data.data?.overview) {
      throw new Error("Analytics overview response was empty");
    }
    return data.data.overview;
  } catch (error) {
    throw new Error(
      `Failed to load analytics overview: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * A lightweight per-session drill-down row (Phase 2a). Backs the click-through
 * from an Analytics panel (day / punchcard cell / agent / project) to its
 * sessions. `name` = userTitle ?? session name; `agentName` is resolved
 * ("Unknown" when metadata.agentName is null/empty); timestamps are ISO-8601 UTC
 * or null. Duration is client-derivable as endedAt - startedAt.
 */
export interface AnalyticsSessionRow {
  id: string;
  /** client_id — the id the session-detail route resolves against, NOT the DB `id`. */
  clientId: string;
  name: string | null;
  /** Raw session filename (before the userTitle override) — for the filename toggle. */
  fileName: string;
  agentName: string;
  startedAt: string | null;
  endedAt: string | null;
  /** Last-synced time — for the "3 hours ago" relative label (matches home). */
  updatedAt: string;
  /** Canonical UTC bucket time = COALESCE(started_at, created_at); always set. */
  bucketIso: string;
  exchangeCount: number;
  projectId: string | null;
  /** Rich-row fields (parity with the home "Recent sessions" card). */
  projectName: string | null;
  projectColor: string | null;
  /** Truncated first-exchange content for the hover preview snippet. */
  firstPrompt: string | null;
}

export interface AnalyticsSessionsParams {
  /** Window start — epoch ms or ISO string. */
  from?: number | string;
  /** Window end (exclusive) — epoch ms or ISO string. */
  to?: number | string;
  /** Resolved agent display name; "Unknown" matches null/empty metadata. */
  agent?: string | null;
  /** projectId filter. */
  project?: string | null;
}

/**
 * Cross-agent Analytics per-session drill-down (Phase 2a). JWT-authed;
 * workspace-scoped. Returns lightweight rows for the click-through session list.
 */
export async function fetchAnalyticsSessions(
  params: AnalyticsSessionsParams,
  ownerId?: string
): Promise<AnalyticsSessionRow[]> {
  try {
    const headers = getAuthHeaders();
    const search = new URLSearchParams();
    if (params.from !== undefined && params.from !== null)
      search.set("from", String(params.from));
    if (params.to !== undefined && params.to !== null)
      search.set("to", String(params.to));
    if (params.agent) search.set("agent", params.agent);
    if (params.project) search.set("project", params.project);

    const qs = search.toString();
    const response = await authedFetch(
      `${analyticsBase(ownerId)}/sessions${qs ? `?${qs}` : ""}`,
      { headers }
    );
    const data: ApiResponse<{ sessions: AnalyticsSessionRow[] }> =
      await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch analytics sessions");
    }
    return data.data?.sessions ?? [];
  } catch (error) {
    throw new Error(
      `Failed to load analytics sessions: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Analytics Ledger (Phase 3) — estimated, public-rate token spend.
//
// Backed by session_token_usage joined to sessions/workspaces, JWT-authed and
// workspace-scoped (see worker handlers/analytics.ts handleAnalyticsLedger).
// These figures are PUBLIC-LIST-RATE ESTIMATES, never a bill. Token-less agents
// (Cursor, Copilot) still get a row with supported=false / zero cost so the
// coverage block stays honest. All amounts are JS numbers (the worker Number()s
// the numeric columns server-side); all dates are ISO-8601 UTC.
// ─────────────────────────────────────────────────────────────────────────────

/** The four token buckets that drive the cost split. */
export interface LedgerTokenBreakdown {
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
}

/** Window-wide rollup. */
export interface AnalyticsLedgerTotals extends LedgerTokenBreakdown {
  estCostUsd: number;
  cacheSavingsUsd: number;
  totalTokens: number;
}

/** One day of spend with its token split (UTC day key). */
export interface LedgerDayPoint extends LedgerTokenBreakdown {
  /** "YYYY-MM-DD" (UTC). */
  date: string;
  estCostUsd: number;
}

/** Spend attributed to one resolved agent ("Unknown" for null/empty). */
export interface LedgerAgentSpend {
  agentName: string;
  estCostUsd: number;
  cacheSavingsUsd: number;
  sessionCount: number;
}

/** Spend attributed to one project (projectId is non-null workspace text). */
export interface LedgerProjectSpend {
  projectId: string;
  projectName: string | null;
  estCostUsd: number;
  sessionCount: number;
}

/** A token-less provider (supported=false) excluded from spend, with its count. */
export interface LedgerExcludedProvider {
  provider: string;
  sessionCount: number;
}

export interface AnalyticsLedgerCoverage {
  /** All sessions in the window (workspace-scoped). */
  totalSessions: number;
  /** Sessions that have a session_token_usage row (any provider). */
  coveredSessions: number;
  /** Rows with supported=true — i.e. sessions that can carry spend. */
  pricedSessions: number;
  /** Token-less providers (Cursor/Copilot et al.) excluded from spend. */
  excludedByProvider: LedgerExcludedProvider[];
}

export interface AnalyticsLedger {
  meta: {
    range: AnalyticsRange;
    /** ISO-8601 UTC window start. */
    fromIso: string;
    /** ISO-8601 UTC window end (exclusive). */
    toIso: string;
    timezone: "UTC";
    /** ISO-8601 UTC time the payload was computed. */
    generatedAtIso: string;
  };
  totals: AnalyticsLedgerTotals;
  spendByDay: LedgerDayPoint[];
  spendByAgent: LedgerAgentSpend[];
  spendByProject: LedgerProjectSpend[];
  coverage: AnalyticsLedgerCoverage;
}

/**
 * Cross-agent Analytics Ledger (Phase 3). JWT-authed; workspace-scoped. Returns
 * estimated public-rate spend aggregates from session_token_usage.
 */
export async function fetchAnalyticsLedger(
  range: AnalyticsRange,
  ownerId?: string,
  filter?: AnalyticsFilterParams
): Promise<AnalyticsLedger> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(
      `${analyticsBase(ownerId)}/ledger?range=${range}${analyticsFilterQuery(filter)}`,
      { headers }
    );
    const data: ApiResponse<{ ledger: AnalyticsLedger }> =
      await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch analytics ledger");
    }
    if (!data.data?.ledger) {
      throw new Error("Analytics ledger response was empty");
    }
    return data.data.ledger;
  } catch (error) {
    throw new Error(
      `Failed to load analytics ledger: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Analytics processing status — poll-friendly derive-pipeline freshness.
//
// Backs the "N recently-synced sessions still processing" indicator. Cheap and
// tab/range-independent, so the analytics shell can poll it without re-running the
// heavy overview/ledger aggregates.
// ─────────────────────────────────────────────────────────────────────────────

export interface AnalyticsProcessing {
  /** Recently-synced sessions missing a token-usage OR spec-score row. */
  pending: number;
  /** Subset of `pending` older than the reconcile interval — likely stuck. */
  stalled: number;
  /** Rollup watermark: derived series are materialized through this instant. */
  derivedThroughIso: string | null;
  generatedAtIso: string;
}

export async function fetchAnalyticsProcessing(
  ownerId?: string
): Promise<AnalyticsProcessing> {
  const headers = getAuthHeaders();
  const response = await authedFetch(`${analyticsBase(ownerId)}/processing`, {
    headers,
  });
  const data: ApiResponse<{ processing: AnalyticsProcessing }> =
    await response.json();
  if (!data.success || !data.data?.processing) {
    throw new Error(data.error || "Failed to fetch analytics processing status");
  }
  return data.data.processing;
}

// ─────────────────────────────────────────────────────────────────────────────
// Analytics Spec Score (Phase 4) — deterministic opening-prompt quality grade.
//
// Mirrors the Ledger contract: JWT-authed, workspace-scoped, ISO-string window
// bounds, empty-but-200. Every figure is a DETERMINISTIC heuristic of the FIRST
// user prompt (the spec), persisted per-session in session_spec_score — never an
// LLM judgement and never of the work itself. The UI keeps an "estimated" tone.
// ─────────────────────────────────────────────────────────────────────────────

export type SpecGrade = "A" | "B" | "C" | "D" | "F";

/** The five rubric dimension keys (snake_case), mirroring the endpoint. */
export type SpecDimensionKey =
  | "constraints"
  | "success_criteria"
  | "verification"
  | "context"
  | "specificity";

export interface SpecScoreMeta {
  range: AnalyticsRange;
  /** ISO-8601 UTC window start. */
  fromIso: string;
  /** ISO-8601 UTC window end (exclusive). */
  toIso: string;
  timezone: "UTC";
  /** ISO-8601 UTC time the payload was computed. */
  generatedAtIso: string;
  /** scorer_version stamped on the rows (badge text). */
  scorerVersion: string;
  /** LLM judge prompt/anchor version (specscore-llm-N). */
  llmScorerVersion: string;
}

/** Headline spec-driven-start metric for the whole window. */
export interface SpecStartRate {
  /** passingSessions / sessionsWithFirstPrompt, 0..1; null when denom 0. */
  rate: number | null;
  /** Sessions that passed (spec-driven start). */
  passingSessions: number;
  /** Sessions whose first prompt was extractable (start-rate denominator). */
  sessionsWithFirstPrompt: number;
  /** All scored rows in window, incl. no-first-prompt sessions (context). */
  totalScored: number;
}

/** One UTC day of the start-rate trend (sparkline source). */
export interface SpecStartRateTrendPoint {
  /** UTC day 'YYYY-MM-DD'. */
  date: string;
  /** passingSessions / sessionsWithFirstPrompt that day; null when denom 0. */
  rate: number | null;
  passingSessions: number;
  sessionsWithFirstPrompt: number;
}

export interface SpecGradeDistributionPoint {
  grade: SpecGrade;
  /** Sessions (with a first prompt) that landed on this grade. */
  sessionCount: number;
}

/** Pass rate for one rubric dimension; denominator = sessionsWithFirstPrompt. */
export interface SpecPerDimensionPoint {
  dimension: SpecDimensionKey;
  passCount: number;
  /** passCount / sessionsWithFirstPrompt, 0..1; null when denom 0. */
  passRate: number | null;
}

/** One binary judge sub-check, with the quoted opener span behind a true answer. */
export interface SpecJudgeCheck {
  id: string;
  answer: boolean;
  evidence: string | null;
}

/** The judge's full output for one rubric dimension. */
export interface SpecJudgeDimension {
  checks: SpecJudgeCheck[];
  assessment: string;
}

/** A low-scoring opener worth surfacing for coaching (worst offenders). */
export interface SpecScoreSample {
  sessionId: string;
  /** client_id — the id the session-detail route resolves against, NOT the DB `id`. */
  clientId: string;
  /** EFFECTIVE score/grade: the LLM judge's when scoredBy="llm", else the rubric's. */
  score: number;
  grade: SpecGrade;
  /** Which scorer produced score/grade for this row. */
  scoredBy: "llm" | "rubric";
  /** LLM judge's plain-language "why this grade"; null on rubric-only rows. */
  rationale: string | null;
  /** Judge task-intent gate: implementation | exploration | trivial-directive; null on rubric rows. */
  llmIntent: string | null;
  /** Full judge scorecard keyed by dimension (goal_clarity, …); null on rubric rows. */
  dimEvidence: Record<string, SpecJudgeDimension> | null;
  /** Number of byte-identical openers this row represents (>= 1). */
  duplicateCount: number;
  /** Resolved agent label ('Unknown' when unattributed). */
  agentName: string;
  projectId: string | null;
  /** Truncated first-prompt excerpt; null when not retained. */
  firstPromptExcerpt: string | null;
  /** Rubric dimension keys the opener missed (snake_case). */
  missingDims: SpecDimensionKey[];
  /** ISO-8601 UTC bucket time of the session; null when unknown. */
  bucketIso: string | null;
}

export interface AnalyticsSpecScore {
  meta: SpecScoreMeta;
  startRate: SpecStartRate;
  startRateTrend: SpecStartRateTrendPoint[];
  gradeDistribution: SpecGradeDistributionPoint[];
  perDimension: SpecPerDimensionPoint[];
  offendingSamples: SpecScoreSample[];
  /** Highest-scoring passing openers (strong opens), capped ~8 (matches offenders). */
  exemplarSamples: SpecScoreSample[];
}

/**
 * Cross-agent Spec Score (Phase 4 — deterministic opening-prompt grade). Same
 * auth + caching shape as fetchAnalyticsLedger; keyed on `range`.
 */
export async function fetchAnalyticsSpecScore(
  range: AnalyticsRange,
  ownerId?: string,
  filter?: AnalyticsFilterParams
): Promise<AnalyticsSpecScore> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(
      `${analyticsBase(ownerId)}/spec-score?range=${range}${analyticsFilterQuery(filter)}`,
      { headers }
    );
    const data: ApiResponse<{ specScore: AnalyticsSpecScore }> =
      await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch spec score");
    }
    if (!data.data?.specScore) {
      throw new Error("Spec score response was empty");
    }
    return data.data.specScore;
  } catch (error) {
    throw new Error(
      `Failed to load spec score: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/** Paginated grade-filtered spec-score session browser payload. */
export interface AnalyticsSpecScoreSessions {
  range: AnalyticsRange;
  grade: SpecGrade;
  total: number;
  offset: number;
  limit: number;
  sessions: SpecScoreSample[];
}

/**
 * Every session in the window whose effective spec-score grade matches, newest
 * first. Backs the click-a-grade-bar browser in the Spec Score panel.
 */
export async function fetchAnalyticsSpecScoreSessions(
  range: AnalyticsRange,
  grade: SpecGrade,
  offset = 0,
  limit = 25,
  ownerId?: string
): Promise<AnalyticsSpecScoreSessions> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(
      `${analyticsBase(ownerId)}/spec-score/sessions?range=${range}&grade=${grade}&offset=${offset}&limit=${limit}`,
      { headers }
    );
    const data: ApiResponse<{ specScoreSessions: AnalyticsSpecScoreSessions }> =
      await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch spec score sessions");
    }
    if (!data.data?.specScoreSessions) {
      throw new Error("Spec score sessions response was empty");
    }
    return data.data.specScoreSessions;
  } catch (error) {
    throw new Error(
      `Failed to load spec score sessions: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

export async function chatWithRAGStreaming(
  query: string,
  chatSessionId?: string,
  projectIds?: string[],
  onChunk?: (chunk: string) => void,
  onStep?: (step: StreamingStepEvent) => void,
  onContext?: (context: StreamingContextEvent) => void,
  timeFilter?: TimeFilter,
  agentNames?: string[],
  onError?: (error: string) => void
): Promise<RAGResponse> {
  try {
    const headers = getAuthHeaders();
    const response = await authedFetch(`${API_BASE}/api/v1/chat`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        query,
        chatSessionId,
        projectIds,
        timeFilter,
        agentNames,
        stream: true, // Enable streaming
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.error || `Chat request failed: ${response.statusText}`
      );
    }

    // Check if we got a streaming response
    const contentType = response.headers.get("content-type");
    if (
      contentType?.includes("application/x-ndjson") ||
      contentType?.includes("text/plain") ||
      contentType?.includes("text/event-stream")
    ) {
      // Handle streaming response with newline-delimited JSON
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body available for streaming");
      }

      const decoder = new TextDecoder();
      let fullResponse = "";
      let receivedSessionId = chatSessionId || "";
      let buffer = ""; // Buffer for incomplete lines

      const parseLine = (line: string) => {
        try {
          const parsed = JSON.parse(line);

          // Extract data from the event
          const { data } = parsed;

          if (data) {
            const { chatSessionId, response, type } = data;

            // Extract session ID if present (should be in first chunk)
            if (chatSessionId) {
              receivedSessionId = chatSessionId;
            }

            // Handle different event types based on actual API
            if (type === "chat.stream.chunk" && response) {
              // Text response chunks
              const responseText = response;
              fullResponse += responseText;

              // Call the callback with just the text chunk
              if (onChunk) {
                onChunk(responseText);
              }
            } else if (type === "chat.stream.event" && response) {
              // Step/status events - these are our RAG process steps!
              if (onStep) {
                onStep({
                  type: type,
                  message: response,
                  sessionId: chatSessionId,
                });
              }
            } else if (type === "chat.stream.query_rewritten") {
              // Query rewrite event with original and rewritten queries
              if (onStep) {
                onStep({
                  type: "query_rewrite",
                  message: response || "Query rewritten",
                  originalQuery: data.originalQuery,
                  rewrittenQuery: data.rewrittenQuery,
                  sessionId: chatSessionId,
                });
              }
            } else if (type === "chat.stream.embedding_search_results") {
              // Context/search results with exchanges
              if (onContext) {
                onContext(data);
              }
            } else if (type === "chat.stream.error") {
              // Error event from the RAG API
              const errorMessage =
                response || "An error occurred during chat processing";
              if (onError) {
                onError(errorMessage);
              }
              // Don't throw here - let the error handler update UI
              // The stream will end naturally with chat.stream.end
            } else if (type === "chat.stream.start") {
              // Stream started
            } else if (type === "chat.stream.end") {
              // Stream ended
            }
          }
        } catch (parseError) {
          console.warn("Failed to parse streaming chunk:", line, parseError);
          if (onChunk) {
            onChunk(line);
          }
        }
      };

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Process complete lines (newline-delimited JSON)
          const lines = buffer.split("\n");
          // Keep the last potentially incomplete line in the buffer
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.trim()) {
              parseLine(line);
            }
          }
        }

        // Process any remaining buffer content
        if (buffer.trim()) {
          parseLine(buffer);
        }
      } finally {
        reader.releaseLock();
      }

      // Return the complete response with the received session ID
      const result = {
        question: query,
        rewritten_query: null,
        response: fullResponse,
        context_chunks: [],
        metadata: {
          num_chunks: 0,
          projects_searched: projectIds || [],
          session_id: receivedSessionId, // Use the session ID from the stream
          used_reranking: false,
        },
      };
      return result;
    } else {
      // Fallback to non-streaming response
      const data: ApiResponse<RAGResponse> = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Chat request failed");
      }

      if (typeof data.data === "string") {
        return {
          question: query,
          rewritten_query: null,
          response: data.data,
          context_chunks: [],
          metadata: {
            num_chunks: 0,
            projects_searched: projectIds || [],
            session_id: chatSessionId || "",
            used_reranking: false,
          },
        };
      }

      return data.data!;
    }
  } catch (error) {
    throw new Error(
      `Chat request failed: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

// ── Cloud Lore (P1): /skills review service fns ──────────────────────────────
export interface LoreDossier {
  id: string;
  clusterKey: string;
  kind: string | null;
  verdict: string | null;
  status: string;
  skillMd: string | null;
  createdAt: string | null;
  json: { name?: string; trigger?: string } | null;
}

export async function listLoreDossiers(
  status: string = "pending"
): Promise<LoreDossier[]> {
  const headers = getAuthHeaders();
  const response = await authedFetch(
    `${API_BASE}/api/v1/lore/dossiers?status=${encodeURIComponent(status)}`,
    { headers }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to list dossiers");
  return data.dossiers || [];
}

export async function approveDossier(id: string): Promise<void> {
  const headers = getAuthHeaders();
  const response = await authedFetch(`${API_BASE}/api/v1/lore/dossiers/${id}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify({ action: "approve" }),
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
}

export async function declineDossier(id: string, note: string): Promise<void> {
  const headers = getAuthHeaders();
  const response = await authedFetch(`${API_BASE}/api/v1/lore/dossiers/${id}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify({ action: "decline", note }),
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
}

// ── Cloud Lore (P2): "Run now" trigger ───────────────────────────────────────
// Operator-only. `ownerId` defaults server-side to the caller's own owner id.
export async function triggerLoreRun(
  ownerId?: string
): Promise<{ runId: string }> {
  const headers = getAuthHeaders();
  const response = await authedFetch(`${API_BASE}/api/v1/lore/run`, {
    method: "POST",
    headers,
    body: JSON.stringify(ownerId ? { ownerId } : {}),
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }
  return { runId: data.runId };
}

// ── Cloud Lore (P10): forged-skill down-channel + run history ─────────────────
export interface ForgedSkill {
  name: string;
  skillMd: string | null;
  clusterKey: string | null;
  fingerprint: string | null;
  contentSha: string | null;
  installState: string;
  // P4.10: the seven-branch drift recommendation (cloudForgedCheck), when the API has
  // current cluster state to compute it. Absent until that down-channel state is wired.
  recommendation?: string;
}

export interface LoreRunShard {
  shardKey: string;
  scopeKind: string;
  scope: { project_id?: string; project_ids?: string[] } | null;
  status: string;
  sandboxId: string | null;
  sessionCount: number | null;
  spanCount: number | null;
  startedAt: string | null;
  endedAt: string | null;
  error: string | null;
}

export interface LoreRunDossier {
  name: string;
  clusterKey: string;
  verdict: string;
  confidence: string | null;
  adjudicated: boolean;
  hasSkill: boolean;
}

export interface LoreRun {
  id: string;
  status: string;
  trigger: string;
  shardCount: number;
  shardsDone: number;
  sessionCount: number | null;
  spanCount: number | null;
  watermarkFrom: string | null;
  watermarkTo: string | null;
  createdAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  updatedAt: string | null;
  error: string | null;
  sessionsMined: number;
  shards: LoreRunShard[];
  dossierVerdicts: Record<string, number>;
  dossierTotal: number;
  dossiers: LoreRunDossier[];
}

// Cloud Skills IA — the unified library the consolidated Skills workspace renders: forged
// (Ready/Installed) + pending needs-edits (Review), merged server-side into one SkillRow shape.
export type { SkillRow } from "../../../shared/services/lore/skill_row";
// When `ownerId` is provided, reads another user's library via the superadmin admin
// endpoint (email-gated server-side); otherwise reads the caller's own library.
export async function listSkillLibrary(
  ownerId?: string
): Promise<import("../../../shared/services/lore/skill_row").SkillRow[]> {
  const headers = getAuthHeaders();
  const url = ownerId
    ? `${API_BASE}/api/v1/admin/users/${encodeURIComponent(ownerId)}/skills/library`
    : `${API_BASE}/api/v1/lore/skills/library`;
  const response = await authedFetch(url, { headers });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to list skills");
  return data.skills || [];
}

export async function listForgedSkills(
  installState: string = "available"
): Promise<ForgedSkill[]> {
  const headers = getAuthHeaders();
  const response = await authedFetch(
    `${API_BASE}/api/v1/lore/skills?install_state=${encodeURIComponent(installState)}`,
    { headers }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to list skills");
  return data.skills || [];
}

export async function setForgedSkillInstallState(
  name: string,
  installState: string
): Promise<void> {
  const headers = getAuthHeaders();
  const response = await authedFetch(
    `${API_BASE}/api/v1/lore/skills/${encodeURIComponent(name)}`,
    {
      method: "PATCH",
      headers,
      body: JSON.stringify({ install_state: installState }),
    }
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
}

// When `ownerId` is provided, reads another user's run activity via the superadmin
// admin endpoint (email-gated); otherwise reads the caller's own runs.
export async function listLoreRuns(ownerId?: string): Promise<LoreRun[]> {
  const headers = getAuthHeaders();
  const url = ownerId
    ? `${API_BASE}/api/v1/admin/users/${encodeURIComponent(ownerId)}/skills/runs`
    : `${API_BASE}/api/v1/lore/runs`;
  const response = await authedFetch(url, { headers });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to list runs");
  return data.runs || [];
}

// Superadmin: trigger skill generation for another user (the admin "Run skills"
// action). Email-gated server-side; overrides operator allowlist + entitlement.
export async function adminRunUserSkills(
  userId: string
): Promise<{ runId: string }> {
  const headers = getAuthHeaders();
  const response = await authedFetch(
    `${API_BASE}/api/v1/admin/users/${encodeURIComponent(userId)}/skills/run`,
    { method: "POST", headers, body: "{}" }
  );
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || `HTTP error! status: ${response.status}`);
  }
  return { runId: data.runId };
}

// ---- Billing (Stripe) ----

export interface SubscriptionInfo {
  stripe_subscription_id: string;
  plan: string | null;
  status: string;
  current_period_end: string | null;
  trial_end: string | null;
  cancel_at: string | null;
}

export interface CheckoutResult {
  url?: string; // hosted Stripe Checkout URL to redirect to
  alreadySubscribed?: boolean; // user already has an active/trialing sub
}

// Start a hosted Stripe Checkout for the given plan. If the user is already
// subscribed, returns { alreadySubscribed: true } instead of a URL.
export async function createCheckoutSession(
  plan: string,
): Promise<CheckoutResult> {
  const headers = getAuthHeaders();
  const response = await authedFetch(`${API_BASE}/api/v1/billing/checkout`, {
    method: "POST",
    headers,
    body: JSON.stringify({ plan }),
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to start checkout");
  return {
    url: data.data?.url,
    alreadySubscribed: data.data?.alreadySubscribed === true,
  };
}

// Open the Stripe Customer Portal (manage / cancel); returns the URL to redirect to.
export async function createPortalSession(): Promise<string> {
  const headers = getAuthHeaders();
  const response = await authedFetch(`${API_BASE}/api/v1/billing/portal`, {
    method: "POST",
    headers,
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  if (!data.success || !data.data?.url)
    throw new Error(data.error || "Failed to open billing portal");
  return data.data.url as string;
}

export interface Entitlement {
  plan: Plan;
  features: Partial<Record<Feature, boolean>>;
}

// The signed-in user's derived entitlement (plan + per-feature flags). Used to
// decorate the UI; the Worker is the actual enforcement boundary.
export async function fetchEntitlement(): Promise<Entitlement> {
  const headers = getAuthHeaders();
  const response = await authedFetch(
    `${API_BASE}/api/v1/billing/entitlement`,
    { headers },
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to load entitlement");
  return data.data as Entitlement;
}

/**
 * Environment feature flags (Cloudflare Flagship) for the signed-in caller. Drives
 * the SPA's dark-launch hiding of in-flux surfaces. Separate from the entitlement
 * read; the Worker is the authoritative gate, so a stale/failed fetch just leaves
 * the caller with the fail-closed (all-hidden) default.
 */
export async function fetchFlags(): Promise<FlagMap> {
  const headers = getAuthHeaders();
  const response = await authedFetch(`${API_BASE}/api/v1/flags`, { headers });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to load flags");
  return (data.data as { flags: FlagMap }).flags;
}

// The signed-in user's current subscription (or null).
export async function fetchSubscription(): Promise<SubscriptionInfo | null> {
  const headers = getAuthHeaders();
  const response = await authedFetch(
    `${API_BASE}/api/v1/billing/subscription`,
    { headers },
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  if (!data.success) throw new Error(data.error || "Failed to load subscription");
  return (data.data?.subscription ?? null) as SubscriptionInfo | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Superadmin (SpecStory staff only)
// ─────────────────────────────────────────────────────────────────────────────

// A user who synced a session within the trailing window, with identity +
// activity summary. Mirrors the worker's AdminActiveUser shape.
export interface AdminActiveUser {
  userId: string;
  email: string | null;
  totalSessions: number;
  lastSyncedAt: string;
  lastSessionName: string | null;
  skillsCount: number;
  runsCount: number;
  lastRunAt: string | null;
}

// Users who synced a session in the past 24h. The endpoint is gated server-side
// by the @specstory.com email domain; a non-staff caller gets a 403.
export async function fetchAdminActiveUsers(): Promise<AdminActiveUser[]> {
  const headers = getAuthHeaders();
  const response = await authedFetch(
    `${API_BASE}/api/v1/admin/active-users`,
    { headers },
  );
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  if (!data.success)
    throw new Error(data.error || "Failed to load active users");
  return (data.data?.users ?? []) as AdminActiveUser[];
}
